import { useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { logOut } from "../services/AuthService";
import { useUserContext } from "../context/UserContext";
import { useTranslation } from "../context/LanguageContext";
import { useConversionJourney } from "../hooks/useConversionJourney";
import useExamplePreview from "../hooks/useExamplePreview";
import usePageBackground from "../hooks/usePageBackground";
import { conversionErrorProperties } from "../utils/conversionJourney";
import { track } from "../utils/analytics";
import { reportOnboardingStep } from "../services/LifecycleService";
import { dealsFromPreview } from "../utils/onboardingCoverage";
import ZipStepScreen from "../components/onboarding/ZipStepScreen";
import { C } from "../components/onboarding/DealsFirstParts";
import {
  destinationAfterDealIntent,
  hasSavedZip,
  isDealIntent,
} from "../utils/dealOnboarding";

/**
 * Deals-first onboarding, step 1: "See the clearance hiding near you" with the
 * ZIP and language on one screen (this replaced the old purple "Deals from
 * stores near you" intro and the separate ZIP page). Saving the ZIP moves on
 * to /setup, where the plan step opens on this ZIP's own deals.
 */
export default function OnboardingPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { user, updateUser } = useUserContext();
  const params = new URLSearchParams(window.location.search);
  const localIntentRequested = params.get("intent") === "local";
  const requestedZip = params.get("zip")?.replace(/\D/g, "").slice(0, 5) || "";
  const needsChooser =
    !!user?.verified && !user.retailers?.length && !user.setUp && !isDealIntent(user.dealIntent) && !localIntentRequested;
  const journey = useConversionJourney(needsChooser ? "onboarding_zip" : null, "onboarding");
  const [zip, setZip] = useState(() => (hasSavedZip(user?.zipCode) ? user!.zipCode! : ""));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const examplePreview = useExamplePreview(!localIntentRequested);
  usePageBackground(C.page);
  const exampleDeals = useMemo(
    () => (examplePreview === undefined ? undefined : dealsFromPreview(examplePreview)),
    [examplePreview],
  );

  useEffect(() => {
    track("onboarding_viewed");
  }, []);

  useEffect(() => {
    if (needsChooser) reportOnboardingStep("deal_mode");
  }, [needsChooser]);

  // In-app "unlock local" links land here for accounts that already chose the
  // free lane. Pinto sends those straight to the paywall; do the same, carrying
  // any ZIP the shopper typed into a preview so the paywall can skip that step.
  useEffect(() => {
    if (!localIntentRequested || !user?.verified || user.retailers?.length) return;
    let cancelled = false;
    const forward = async () => {
      if (hasSavedZip(requestedZip) && !hasSavedZip(user.zipCode)) {
        try {
          await updateUser({ zipCode: requestedZip });
          track("onboarding_zip_saved");
        } catch {
          /* The paywall asks for a ZIP again if this did not stick. */
        }
      }
      if (!cancelled) navigate("/setup", { replace: true });
    };
    void forward();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localIntentRequested]);

  if (user && !user.verified) {
    return <Navigate to="/verify" replace />;
  }
  if (user?.retailers?.length) {
    return <Navigate to="/" replace />;
  }
  if (localIntentRequested) {
    return null;
  }
  if (user && (user.setUp || isDealIntent(user.dealIntent))) {
    return <Navigate to={destinationAfterDealIntent(isDealIntent(user.dealIntent) ? user.dealIntent : "online")} replace />;
  }

  // Store setup is exploratory until a plan is bought: save only the ZIP (no
  // deal intent), so backing out of the plans returns here.
  const submit = async () => {
    if (!/^\d{5}$/.test(zip)) {
      journey.event("invalid_zip");
      setError(t("onboarding2.zip_invalid"));
      return;
    }
    setError(null);
    journey.event("intent_local_selected");
    track("onboarding_intent_selected", { intent: "instore" });
    track("onboarding_deal_mode_selected", { mode: "local", tier: "paid" });
    setSaving(true);
    try {
      if (zip !== user?.zipCode) await updateUser({ zipCode: zip });
      track("onboarding_zip_saved");
      journey.event("step_saved");
      navigate("/setup");
    } catch (err) {
      journey.event("save_failed", conversionErrorProperties(err));
      setError(t("onboarding2.save_failed"));
      setSaving(false);
    }
  };

  const signOut = () => {
    queryClient.clear();
    logOut();
  };

  return (
    <ZipStepScreen
      zip={zip}
      onZipChange={(value) => {
        setError(null);
        setZip(value);
      }}
      onSubmit={() => void submit()}
      busy={saving}
      error={error}
      onSignOut={signOut}
      deals={exampleDeals}
    />
  );
}
